// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogTitle, Divider, IconButton } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import { connect } from 'react-redux';

// Internal Dependencies
import { IsEmpty } from '../../../common/checks';
import Colours from '../../../styles/colours';
import ProgressiveImage from '../../../components/ProgressiveImage';
import MButton from '../../../components/MButton';
import MText from '../../../components/Common/MText';
import MTextField from '../../../components/MTextField';
import { imageResizeUrl } from '../../../common/convert';
import Icons from '../../../components/Common/Icons';
import MaterialDatePicker from '../../../components/Common/form/MaterialDatePicker';
import MapLocationSelect from './SnapstyrComs/Poi/MapLocationSelect';
import PickPoiLocationModal from './SnapstyrComs/Poi/PickPoiLocationModal';
import PoiMarkerIcon from './SnapstyrComs/Poi/PoiMarkerIcon';
import PhotoLocationIcon from './SnapstyrComs/PhotoLocationIcon';
import PoiTags from './PoiTags';
import ApiService from '../../../services/apiService';
import Overloading from '../../../components/Loading';

const toCoord = (location) => {
  if (IsEmpty(location?.lat) || IsEmpty(location?.lng)) {
    return null;
  }
  return {
    lat: parseFloat(location.lat),
    lng: parseFloat(location.lng),
  };
};

const PhotoDetailsModal = (props) => {
  const [takenOn, setTakenOn] = useState(null);
  const [location, setLocation] = useState(null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);

  const [showPickLocationModal, setShowPickLocationModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const poiCoord = toCoord(props.poiCoord);

  // Initialise the editable fields whenever a different photo is opened.
  useEffect(() => {
    const info = props.photo?.info || {};
    setTakenOn(!IsEmpty(info.takenOn) ? new Date(info.takenOn) : null);
    setLocation(toCoord(info.location));
    setDescription(info.text || '');
    setTags(!IsEmpty(props.photo?.tags) ? props.photo?.tags : []);
  }, [props.photo]);

  const onDeletePhoto = async () => {
    try {
      setIsLoading(true);

      if (!IsEmpty(props.albumId)) {
        await ApiService.deletePageAlbumPhoto(props.page.id, props.albumId, props.photo.id);
      } else {
        await ApiService.deletePhoto(props.page.id, props.photo.id);
      }
      toast.success('Photo deleted');

      setShowDeleteConfirm(false);
      props.onMutationComplete();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdatePhoto = async () => {
    try {
      setIsLoading(true);

      const payload = {
        takenOn:   !IsEmpty(takenOn) ? moment(takenOn).utcOffset('+08').format('YYYY-MM-DD') : null,
        text:      description,
        lat:       location?.lat ?? null,
        lng:       location?.lng ?? null,
        photoTags: tags || [],
      };

      if (!IsEmpty(props.albumId)) {
        await ApiService.updatePageAlbumPhoto(props.page.id, props.albumId, props.photo.id, payload);
      } else {
        await ApiService.updatePoiPhoto(props.page.id, props.photo.id, payload);
      }

      toast.success('Photo info updated');
      props.onMutationComplete();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => props.onClose();

  return (
    <>
      <Dialog
        open={props.open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="body"
        PaperProps={{
          style: {
            borderRadius: 10,
            padding:      0,
            overflowY:    'visible',
            overflow:     'hidden',
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: Colours.TranslucentDarkBox,
          },
        }}
      >
        <div className="w-100 d-flex flex-column pb-4">
          <div className="position-relative d-flex flex-column pb-4">
            <div className="d-flex align-items-center justify-content-end">
              <IconButton disabled={props.disabled} onClick={() => setShowDeleteConfirm(true)}>
                <DeleteOutline
                  style={{
                    color:    props.disabled ? Colours.Gray2 : Colours.Black,
                    fontSize: 18,
                  }}
                />
              </IconButton>

              <MButton
                onClick={() => handleClose()}
                disableRipple
                className="px-4 py-3"
                variant="text"
                colour={Colours.Transparent}
                icon={() => <Icons.Cross color={Colours.Black} fontSize={20} />}
              />
            </div>

            <Divider />

            <div className="mt-3 w-100">
              {
                !IsEmpty(props.photo?.original) && (
                  <ProgressiveImage
                    image={props.photo.original}
                    width="100%"
                    height="auto"
                    draggable={false}
                  />
                )
              }
            </div>

            <div className="w-100 d-flex flex-column pt-2 px-4">
              {
                !IsEmpty(props.photo?.username) && (
                  <div
                    className="d-flex flex-row align-items-center position-relative"
                    style={{ columnGap: 4 }}
                  >
                    <MText
                      text="©"
                      size={11}
                    />
                    {
                      !IsEmpty(props.photo?.pageLogo) && (
                        <ProgressiveImage
                          image={imageResizeUrl(props.photo.pageLogo, 24, 24)}
                          width="1rem"
                          height="1rem"
                          style={{
                            borderRadius: 99,
                            objectFit:    'cover',
                            display:      'block',
                          }}
                        />
                      )
                    }
                    <MText
                      text={`${props.photo.username}`}
                      size={11}
                    />
                  </div>
                )
              }

              {IsEmpty(props.albumId) && (
                <>
                  <div className="d-flex flex-column pt-3 px-3">
                    <div className="d-flex flex-row justify-content-center align-items-center pb-2">
                      <MText
                        size={12}
                        className="mr-1"
                        text="Location where photo was taken"
                      />
                      <Icons.PhotoLocation />
                    </div>

                    <MapLocationSelect
                      className="mt-0"
                      height="20dvh"
                      displayOnly
                      GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
                      staticMarkersData={
                        !IsEmpty(poiCoord)
                          ? [{ latitude: poiCoord.lat, longitude: poiCoord.lng }]
                          : []
                      }
                      staticMarkerIcon={PoiMarkerIcon}
                      markerIcon={() => <PhotoLocationIcon withShadow />}
                      currentCoord={location}
                      mapCenterCoord={!IsEmpty(poiCoord) ? poiCoord : location}
                      onClick={() => setShowPickLocationModal(true)}
                    />
                  </div>

                  <div className="d-flex flex-column justify-content-center align-items-center mt-5">
                    <MText text="Photo taken date" size={12} style={{ lineHeight: '0.9rem' }} />
                    <MaterialDatePicker
                      variant="outlined"
                      className="mt-1"
                      maxDate={new Date()}
                      value={takenOn}
                      onChange={(date) => setTakenOn(date)}
                    />
                  </div>

                  <div className="w-100 d-flex flex-column justify-content-center align-items-center mt-3 px-3">
                    <MText text="Description" size={12} />
                    <MText text="(max. 1000 characters)" colour={Colours.DarkGray} size={11} style={{ lineHeight: '0.9rem' }} />
                    <MTextField
                      className="mt-1 w-100"
                      minRows={6}
                      maxRows={8}
                      value={description}
                      placeholder=""
                      onChange={(value) => setDescription(value)}
                      maxCharacters={1000}
                      showCharactersCount
                    />
                  </div>

                  <div className="w-100 d-flex flex-column justify-content-center align-items-center mt-0 px-3">
                    <MText text="Tags" size={12} />
                    <MText
                      text="(tap 'Enter' after each Tag)"
                      className="mb-1"
                      colour={Colours.DarkGray}
                      size={11}
                      style={{ lineHeight: '0.9rem' }}
                    />
                    <PoiTags
                      userTags={tags}
                      onUserTagsChange={(newTags) => setTags(newTags)}
                      textColour={Colours.Black}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {IsEmpty(props.albumId) && (
            <div className="mb-4 d-flex flex-row justify-content-center">
              <MButton
                className="px-5"
                text="Update"
                textColour={Colours.White}
                variant="contained"
                colour={props.disabled ? Colours.Gray2 : Colours.Secondary}
                onClick={onUpdatePhoto}
                disabled={isLoading || props.disabled}
              />
            </div>
          )}
        </div>
      </Dialog>

      <PickPoiLocationModal
        GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
        open={showPickLocationModal}
        markerIcon={() => <PhotoLocationIcon withShadow size="20px" />}
        existingMarker={location}
        mapCenterCoord={poiCoord}
        staticMarkerIcon={PoiMarkerIcon}
        staticMarkersData={
          !IsEmpty(poiCoord)
            ? [{ latitude: poiCoord.lat, longitude: poiCoord.lng }]
            : []
        }
        onClose={() => setShowPickLocationModal(false)}
        onLocationChange={(newLocation) => {
          setLocation(!IsEmpty(newLocation) ? { lat: newLocation.lat, lng: newLocation.lng } : null);
        }}
      />

      <Dialog
        open={showDeleteConfirm}
        fullWidth
        maxWidth="sm"
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>
          <div className="w-100 d-flex flex-column">
            <MText
              text="Confirmation"
              size={15}
            />
            <Divider className="my-2" />
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="d-flex flex-column pb-3">
            <MText
              text="Delete photo? Action is irreversible"
              semibold
              size={13}
            />
          </div>

          <Divider className="my-3" />

          <div className="pb-3 d-flex flex-row justify-content-between">
            <MButton
              text="Cancel"
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
            />
            <MButton
              text="Delete"
              colour={Colours.Red}
              textColour={Colours.White}
              onClick={onDeletePhoto}
              disabled={isLoading}
            />
          </div>
        </DialogContent>
      </Dialog>
      {isLoading && <Overloading />}
    </>
  );
};

PhotoDetailsModal.defaultProps = {
  open:               false,
  disabled:           false,
  onClose:            () => { },
  photo:              {},
  poiCoord:           null,
  albumId:            null,
  onMutationComplete: () => {},
};

PhotoDetailsModal.propTypes = {
  open:     PropTypes.bool,
  disabled: PropTypes.bool,
  onClose:  PropTypes.func,
  page:     PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  photo: PropTypes.shape({
    id:       PropTypes.number,
    original: PropTypes.string,
    username: PropTypes.string,
    info:     PropTypes.shape({
      takenOn:  PropTypes.string,
      text:     PropTypes.string,
      location: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
    }),
    tags:     PropTypes.arrayOf(PropTypes.string),
    pageId:   PropTypes.number,
    pageLogo: PropTypes.string,
  }),
  poiCoord: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  albumId:            PropTypes.number,
  onMutationComplete: PropTypes.func,
};

const mapStateToProps = (state) => ({
  page: state.page.selected,
});

export default connect(mapStateToProps, null)(PhotoDetailsModal);
